import { useForm } from 'react-hook-form';
import { allCountries } from 'country-telephone-data';
import {
  DarwinCol,
  DarwinRow,
  DarwinDivider,
  DarwinButton,
  DarwinSpin,
  DarwinFlex,
} from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { EnvironmentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from '../Organizations.module.css';
import { GenericInput } from '../../GenericInput';
import { ErrorType, InputType } from 'types/genericTypes';
import {
  EMAIL_REGEX_VALIDATION,
  ORGANIZATION_INPUT_MAX_LENGTH,
  ORGANIZATION_PHONE_MAX_LENGTH,
  ORGANIZATION_TEXT_AREA_MAX_LENGTH,
  ORGANIZATION_EXTERNAL_ID_INPUT_MAX_LENGTH,
} from 'utils/organization';
import {
  AddressBookSectionFormData,
  AddressBookSectionInputProps,
  CompanyAddress,
  CreateLocationPayload,
  Location,
  LocationInputProps,
} from 'types/organizationTypes';
import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { AddressBookEmpty } from './AddressBookEmpty';
import { DarwinModal } from '../../../darwin-ui/Modal';
import { useCreateOrganizationFormContext } from 'contexts/CreateOrganizationFormContext';
import {
  useCreateAddressForExternalOrganization,
  useCreateLocationForAddress,
  useDeleteAddressForExternalOrganization,
  useDeleteLocationForAddress,
  useGetContactAutoComplete,
  useUpdateAddressForExternalOrganization,
  useUpdateLocationForAddress,
} from 'api/services/organizationService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { AddressBookAddressesCollapse } from './AddressBookAddressesCollapse';
import { AddressDetails } from './AddressDetails';
import { LocationDetails } from './LocationDetails';
import { useGetFdaProcesses } from 'api/services/processesService';
import { extractQueryData } from 'utils/services';
import { ProcessCategory } from 'types/processesTypes';
import { useDebounce } from '@uidotdev/usehooks';
import { DEBOUNCED_PHONE_AUTOCOMPLETED } from 'utils/common';
import { UploadLocationImage } from './UploadLocationImage';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { featureFlagsNames } from 'utils/featureFlag';

const { Text } = DarwinTypography;

const DEBOUNCED_SEARCH = 700;

const ADDRESS_FORM_DEFAULT_VALUES = {
  addressName: '',
  addressLine1: '',
  addressLine2: '',
  country: '',
  state: '',
  city: '',
  zipCode: '',
  contactName: '',
  contactPhone: { phoneCode: '', phoneNumber: '' },
  contactEmail: '',
};

const LOCATION_FORM_DEFAULT_VALUES = {
  name: '',
  description: '',
  processId: '',
  externalId: '',
  externalUrl: '',
  fileUrl: '',
};

export interface initialOptionsAutoComplete {
  name?: string;
  email?: string;
  phoneCode?: string | undefined;
  phoneNumber?: string | undefined;
}

type AddressBookSectionProps = {
  addresses?: CompanyAddress[];
  handleCreateOrganization: (callback: () => void) => void;
  isCreateForm: boolean;
  targetCompanyId: string;
};

export const AddressBookSection = ({
  addresses,
  handleCreateOrganization,
  isCreateForm,
  targetCompanyId,
}: AddressBookSectionProps): JSX.Element => {
  // common
  const { t } = useTranslation();
  const eudrGeolocation = useFeatureFlag(featureFlagsNames.eudrGeolocation);
  const navigate = useNavigate();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();
  const {
    companyData,
    isEditingAddress,
    setIsEditingAddress,
    isEditingLocation,
    setIsEditingLocation,
    setHasToResetCollapse,
  } = useCreateOrganizationFormContext();

  // address form
  const [displayForm, setDisplayForm] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<CompanyAddress>(
    {} as CompanyAddress
  );
  const [autoCompleting, setAutoCompleting] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AddressBookSectionFormData>({
    defaultValues: ADDRESS_FORM_DEFAULT_VALUES,
    mode: 'onChange',
  });

  const search = watch('contactName');
  const debouncedSearch = useDebounce(search, DEBOUNCED_SEARCH);
  const {
    data: searchProcessFields,
    isLoading: isSearchingProcessFields,
    isFetching: isFetchingProcessFields,
  } = useGetContactAutoComplete({
    companyId: selectedCompany || '',
    targetCompanyId: targetCompanyId || '',
    search: debouncedSearch,
  });

  const autoCompletingSearch =
    isSearchingProcessFields || isFetchingProcessFields;

  //set options to auto complete
  const initialOptionsAutoComplete = useMemo(() => {
    return (
      searchProcessFields?.data?.map(
        (option: initialOptionsAutoComplete, index: number) => ({
          key: index,
          label: option.name,
          value: option.name,
          email: option.email,
          phoneCode: option.phoneCode,
          phoneNumber: option.phoneNumber,
        })
      ) || []
    );
  }, [searchProcessFields?.data]);

  // Auto Complete Prefix
  const onSelectCountry = useCallback(
    (phoneCode: string) => {
      //Validate if we have phoneCode and phoneNumber
      const phone = watch('contactPhone');
      const contactPhone = { phoneCode: phoneCode, phoneNumber: '' };
      if (phone) {
        setValue('contactPhone', {
          phoneCode: '',
          phoneNumber: phone.phoneNumber,
        });
        contactPhone.phoneNumber = phone.phoneNumber;
      }
      setValue('contactPhone', contactPhone);
    },
    [setValue, watch]
  );

  //Auto Complete
  const onAutoComplete = useCallback(
    (
      email: string,
      contactPhone: { phoneCode: string; phoneNumber: string }
    ) => {
      const Email = watch('contactEmail');
      const Phone = watch('contactPhone');
      if (Email && Phone) {
        setValue('contactEmail', '');
        setValue('contactPhone', { phoneCode: '', phoneNumber: '' });
      }
      setAutoCompleting(true);
      setTimeout(() => {
        setValue('contactEmail', email, { shouldValidate: true });
        setValue(
          'contactPhone',
          {
            phoneCode: contactPhone.phoneCode.slice(1),
            phoneNumber: contactPhone.phoneNumber,
          },
          {
            shouldValidate: true,
          }
        );
      }, 1000);
      setTimeout(() => setAutoCompleting(false), DEBOUNCED_PHONE_AUTOCOMPLETED);
    },
    [setValue, watch]
  );

  const { mutateAsync: createAddress, isPending: isCreatingAddress } =
    useCreateAddressForExternalOrganization();

  const { mutateAsync: editAddress, isPending: isEditingAddressPending } =
    useUpdateAddressForExternalOrganization();

  const { mutateAsync: deleteAddress, isPending: isDeletingAddressPending } =
    useDeleteAddressForExternalOrganization();

  const handleUpdateDisplayForm = (updateDisplayForm: boolean) => {
    setDisplayForm(updateDisplayForm);
  };

  const handleSetSelectedAddress = (address: CompanyAddress) => {
    setSelectedAddress(address);
  };

  const { id: addressId, city: cityByAddress } = selectedAddress;

  const handleDeleteAddress = async () => {
    try {
      await deleteAddress({
        companyId: selectedCompany || '',
        targetCompanyId: isCreateForm ? companyData?.id || '' : targetCompanyId,
        addressId: selectedAddress.id,
      });

      openNotification(
        t('success'),
        t('create-organization-form.delete-address-success-message'),
        'success'
      );
      setHasToResetCollapse(true);
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('create-organization-form.delete-address-error-message'),
          'error'
        );
      }
    }
  };

  const onSubmit = async (data: AddressBookSectionFormData) => {
    const { contactName, contactPhone, contactEmail, country, ...rest } = data;

    const payload = {
      ...rest,
      country:
        allCountries.find(count => count.dialCode === country)?.name || '',
      contact: {
        contactName,
        phoneCode: `+${contactPhone?.phoneCode}` || '',
        phoneNumber: contactPhone?.phoneNumber ?? '',
        ...(contactEmail !== '' && { contactEmail }),
      },
    };

    if (isEditingAddress) {
      try {
        await editAddress({
          addressData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: isCreateForm
            ? companyData?.id || ''
            : targetCompanyId,
          addressId: selectedAddress.id,
        });
        openNotification(
          t('success'),
          t('create-organization-form.edit-address-success-message'),
          'success'
        );
        handleUpdateDisplayForm(false);
        setIsEditingAddress(false);
        handleResetFormValues(ADDRESS_FORM_DEFAULT_VALUES);
        setHasToResetCollapse(true);
      } catch {
        openNotification(
          t('error'),
          t('create-organization-form.edit-address-error-message'),
          'error'
        );
      }
    } else {
      try {
        await createAddress({
          addressData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: isCreateForm
            ? companyData?.id || ''
            : targetCompanyId,
        });
        openNotification(
          t('success'),
          t('create-organization-form.create-address-success-message'),
          'success'
        );
        if (isCreateForm) {
          navigate(
            generatePath(ROUTES.ORGANIZATIONS.EDIT, {
              id: companyData?.id || '',
            })
          );
        }
        handleUpdateDisplayForm(false);
        handleResetFormValues(ADDRESS_FORM_DEFAULT_VALUES);
      } catch {
        openNotification(
          t('error'),
          t('create-organization-form.create-address-error-message'),
          'error'
        );
      }
    }
  };

  const handleResetFormValues = (data: AddressBookSectionFormData) => {
    reset(data);
  };

  const firstColumnInputs: AddressBookSectionInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'addressName',
      label: t('create-organization-form.address-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.address-name-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.address-name'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'addressLine1',
      label: t('create-organization-form.primary-address'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.primary-address-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.primary-address'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'addressLine2',
      label: t('create-organization-form.secondary-address'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.secondary-address'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
  ];

  const secondColumnInputs: AddressBookSectionInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'city',
      label: t('create-organization-form.city'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.city-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.city'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'state',
      label: t('create-organization-form.state'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.state'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'country',
      label: t('create-organization-form.country'),
      showRequiredAsterisk: true,
      inputRules: { required: t('create-organization-form.country-required') },
      dropdownData: allCountries.map(country => ({
        id: country.dialCode,
        label: country.name,
        phoneSelectKey: true,
      })),
      onSelectCountry: onSelectCountry,
      enableSearchInDropdown: true,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'zipCode',
      label: t('create-organization-form.zip-code'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.zip-code-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.zip-code'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
  ];

  const thirdColumnInputs: AddressBookSectionInputProps = [
    {
      inputType: InputType.AUTOCOMPLETE,
      fieldName: 'contactName',
      label: t('create-organization-form.contact-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.contact-name-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.contact-name'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      filteredOptions: initialOptionsAutoComplete,
      autoCompleting: autoCompletingSearch,
      onAutoComplete: onAutoComplete,
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'contactEmail',
      label: t('create-organization-form.email'),
      showRequiredAsterisk: false,
      inputRules: {
        pattern: {
          value: EMAIL_REGEX_VALIDATION,
          message: t('create-organization-form.email-format'),
        },
        maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
      autoCompleting: autoCompleting,
    },
    {
      inputType: InputType.PHONE,
      fieldName: 'contactPhone',
      label: t('create-organization-form.phone'),
      showRequiredAsterisk: true,
      autoCompleting: autoCompleting,
      inputRules: {},
      maxLength: ORGANIZATION_PHONE_MAX_LENGTH,
      isCreateForm: !isEditingAddress,
    },
  ];

  // location form
  const [displayLocationForm, setDisplayLocationForm] =
    useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<Location>(
    {} as Location
  );
  const {
    control: controlLocationForm,
    handleSubmit: handleSubmitLocationForm,
    reset: resetLocationForm,
    formState: { errors: errorsLocationForm },
  } = useForm<CreateLocationPayload>({
    defaultValues: LOCATION_FORM_DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { mutateAsync: createLocation, isPending: isCreatingLocation } =
    useCreateLocationForAddress();

  const handleSetSelectedLocation = (location: Location) => {
    setSelectedLocation(location);
  };

  const { mutateAsync: editLocation, isPending: isEditingLocationPending } =
    useUpdateLocationForAddress();

  const { mutateAsync: deleteLocation, isPending: isDeletingLocationPending } =
    useDeleteLocationForAddress();

  const fdaProcessesQuery = useGetFdaProcesses();

  const fdaProcessesData = extractQueryData(fdaProcessesQuery, undefined);

  const handleDeleteLocation = async () => {
    try {
      await deleteLocation({
        companyId: selectedCompany || '',
        targetCompanyId: isCreateForm ? companyData?.id || '' : targetCompanyId,
        addressId: selectedAddress.id,
        locationId: selectedLocation.id,
      });

      openNotification(
        t('success'),
        t('create-organization-form.delete-location-success-message'),
        'success'
      );
      setHasToResetCollapse(true);
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('create-organization-form.delete-location-error-message'),
          'error'
        );
      }
    }
  };

  const onSubmitLocationForm = async (data: CreateLocationPayload) => {
    const { name, description, processId, externalId, externalUrl } = data;
    const payload: CreateLocationPayload = {
      name,
      description,
      processId,
      externalId: externalId || '',
    };

    if (!eudrGeolocation) {
      if (externalUrl && externalUrl.length > 0)
        payload.externalUrl = externalUrl;
      if (imageUrl && imageUrl.length > 0) payload.fileUrl = imageUrl;
    }

    if (isEditingLocation) {
      try {
        await editLocation({
          locationData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: isCreateForm
            ? companyData?.id || ''
            : targetCompanyId,
          addressId: selectedAddress.id,
          locationId: selectedLocation.id,
        });

        openNotification(
          t('success'),
          t('create-organization-form.edit-location-success-message'),
          'success'
        );
        handleUpdateDisplayLocationForm(false);
        setIsEditingLocation(false);
        handleResetLocationFormValues(LOCATION_FORM_DEFAULT_VALUES);
        setHasToResetCollapse(true);
      } catch {
        openNotification(
          t('error'),
          t('create-organization-form.edit-location-error-message'),
          'error'
        );
      }
    } else {
      try {
        await createLocation({
          locationData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: isCreateForm
            ? companyData?.id || ''
            : targetCompanyId,
          addressId: selectedAddress.id,
        });

        openNotification(
          t('success'),
          t('create-organization-form.create-location-success-message'),
          'success'
        );

        handleUpdateDisplayLocationForm(false);
        resetLocationForm();
      } catch {
        openNotification(
          t('error'),
          t('create-organization-form.create-location-error-message'),
          'error'
        );
      }
    }
  };

  const handleResetLocationFormValues = (data: CreateLocationPayload) => {
    resetLocationForm(data);
  };

  const handleUpdateDisplayLocationForm = (
    updateDisplayLocationForm: boolean
  ) => {
    setDisplayLocationForm(updateDisplayLocationForm);
  };

  const locationFormInputs: LocationInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('create-organization-form.location'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.location-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.location'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
    {
      inputType: InputType.DROPDOWN,
      fieldName: 'processId',
      label: t('create-organization-form.process-type'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.process-type-required'),
      },
      dropdownData: (fdaProcessesData as ProcessCategory[])?.flatMap(category =>
        category.processes.map(process => ({
          id: process.id,
          label: process.name,
        }))
      ),
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('create-organization-form.description'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.description-required'),
        maxLength: {
          value: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.description'),
            maxLength: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
      tooltipText: t('create-organization-form.new-location-tooltip-text'),
      tooltipIcon: <InfoCircleOutlined />,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'externalId',
      label: t('create-organization-form.external-id'),
      inputRules: {
        maxLength: {
          value: ORGANIZATION_EXTERNAL_ID_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.location'),
            maxLength: ORGANIZATION_EXTERNAL_ID_INPUT_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_EXTERNAL_ID_INPUT_MAX_LENGTH,
    },
  ];

  if (!eudrGeolocation) {
    locationFormInputs.push({
      inputType: InputType.INPUT,
      fieldName: 'externalUrl',
      label: t('create-organization-form.external-url'),
      inputRules: {},
    });
  }

  return (
    <>
      <DarwinSpin
        spinning={
          isDeletingAddressPending ||
          isDeletingLocationPending ||
          isEditingAddressPending ||
          isEditingLocationPending
        }
        fullscreen
      />
      <Text
        className={`${styles.organizationFormRowTitle} ${styles.organizationFormRowSpacing}`}
      >
        <EnvironmentOutlined className={styles.organizationFormTitleIcon} />{' '}
        {t('create-organization-form.address-book')}
      </Text>
      <DarwinRow className={styles.organizationFirstDetails} gutter={32}>
        {addresses && addresses.length < 1 && (
          <AddressBookEmpty
            handleCreateOrganization={handleCreateOrganization}
            handleUpdateDisplayForm={handleUpdateDisplayForm}
          />
        )}

        {addresses && addresses.length > 0 && (
          <>
            <DarwinCol span={12}>
              <div className={styles.organizationAddAddressOrLocationContainer}>
                <DarwinButton
                  className={styles.organizationAddAddressOrLocationButton}
                  onClick={() => {
                    handleResetFormValues(ADDRESS_FORM_DEFAULT_VALUES);
                    handleUpdateDisplayForm(true);
                  }}
                >
                  {t('create-organization-form.add-address-button')}
                </DarwinButton>
              </div>
              <DarwinDivider />
              <AddressBookAddressesCollapse
                handleSetSelectedAddress={handleSetSelectedAddress}
                addresses={addresses}
                handleSetSelectedLocation={handleSetSelectedLocation}
              />
            </DarwinCol>
            {Object.keys(selectedAddress).length > 0 && (
              <DarwinCol span={12}>
                <div
                  className={styles.organizationAddAddressOrLocationContainer}
                >
                  <DarwinButton
                    className={styles.organizationAddAddressOrLocationButton}
                    onClick={() => {
                      handleResetLocationFormValues(
                        LOCATION_FORM_DEFAULT_VALUES
                      );
                      setImageUrl('');
                      handleUpdateDisplayLocationForm(true);
                    }}
                  >
                    {t('create-organization-form.add-location-button')}
                  </DarwinButton>
                </div>
                <DarwinDivider />
                {Object.keys(selectedLocation).length > 0 ? (
                  <LocationDetails
                    handleResetLocationFormValues={
                      handleResetLocationFormValues
                    }
                    targetCompanyId={targetCompanyId}
                    addressId={addressId}
                    handleUpdateDisplayLocationForm={
                      handleUpdateDisplayLocationForm
                    }
                    location={selectedLocation}
                    cityByAddress={cityByAddress || ''}
                    handleDeleteLocation={handleDeleteLocation}
                  />
                ) : (
                  <AddressDetails
                    handleResetFormValues={handleResetFormValues}
                    handleUpdateDisplayForm={handleUpdateDisplayForm}
                    address={selectedAddress}
                    handleDeleteAddress={handleDeleteAddress}
                  />
                )}
              </DarwinCol>
            )}
          </>
        )}

        {/* address form */}
        {displayForm && (
          <DarwinModal
            open={displayForm}
            onCancel={() => {
              handleResetFormValues(ADDRESS_FORM_DEFAULT_VALUES);
              handleUpdateDisplayForm(false);
              setIsEditingAddress(false);
              if (isCreateForm) {
                navigate(
                  generatePath(ROUTES.ORGANIZATIONS.EDIT, {
                    id: companyData?.id || '',
                  })
                );
              }
            }}
            onOk={handleSubmit(onSubmit)}
            cancelText={t(
              'users-page.users-list.delete-user-modal.cancel-button'
            )}
            okText={t('users-page.users-list.delete-user-modal.confirm-button')}
            confirmLoading={isCreatingAddress || isEditingAddressPending}
            cancelButtonProps={{
              disabled: isCreatingAddress || isEditingAddressPending,
            }}
            centered
            closable={false}
          >
            <Form layout="vertical" onSubmitCapture={handleSubmit(onSubmit)}>
              <DarwinCol span={24}>
                <Text className={styles.organizationFormAddressSectionTitles}>
                  {t('create-organization-form.new-address')}
                </Text>
                <DarwinDivider />
                {firstColumnInputs.map(input => (
                  <GenericInput<AddressBookSectionFormData>
                    key={input.fieldName}
                    control={control}
                    errors={errors}
                    {...input}
                  />
                ))}
                <DarwinRow gutter={24}>
                  <DarwinCol span={12}>
                    {secondColumnInputs.map(input => (
                      <GenericInput<AddressBookSectionFormData>
                        key={input.fieldName}
                        control={control}
                        errors={errors}
                        {...input}
                      />
                    ))}
                  </DarwinCol>
                  <DarwinCol span={12}>
                    {thirdColumnInputs.map(input => (
                      <GenericInput<AddressBookSectionFormData>
                        key={input.fieldName}
                        control={control}
                        errors={errors}
                        {...input}
                      />
                    ))}
                  </DarwinCol>
                </DarwinRow>
              </DarwinCol>
            </Form>
          </DarwinModal>
        )}

        {/* location form */}
        {displayLocationForm && (
          <DarwinModal
            open={displayLocationForm}
            centered
            closable={false}
            footer={[
              <>
                <DarwinFlex
                  style={{
                    justifyContent: !eudrGeolocation ? 'space-between' : 'end',
                  }}
                >
                  {!eudrGeolocation && (
                    <UploadLocationImage setImage={setImageUrl} />
                  )}
                  <DarwinFlex style={{ gap: '8px' }}>
                    <DarwinButton
                      disabled={isCreatingLocation || isEditingLocationPending}
                      onClick={() => {
                        handleResetLocationFormValues(
                          LOCATION_FORM_DEFAULT_VALUES
                        );
                        handleUpdateDisplayLocationForm(false);
                        setIsEditingLocation(false);
                      }}
                    >
                      {t(
                        'users-page.users-list.delete-user-modal.cancel-button'
                      )}
                    </DarwinButton>
                    <DarwinButton
                      type="primary"
                      onClick={handleSubmitLocationForm(onSubmitLocationForm)}
                      loading={isCreatingLocation || isEditingLocationPending}
                    >
                      {t(
                        'users-page.users-list.delete-user-modal.confirm-button'
                      )}
                    </DarwinButton>
                  </DarwinFlex>
                </DarwinFlex>
              </>,
            ]}
          >
            <Form
              layout="vertical"
              onSubmitCapture={handleSubmitLocationForm(onSubmitLocationForm)}
            >
              <DarwinCol span={24}>
                <Text className={styles.organizationFormAddressSectionTitles}>
                  {t('create-organization-form.new-location')}
                </Text>
                <DarwinDivider />
                {locationFormInputs.map(input => (
                  <GenericInput<CreateLocationPayload>
                    key={input.fieldName}
                    control={controlLocationForm}
                    errors={errorsLocationForm}
                    {...input}
                  />
                ))}
              </DarwinCol>
            </Form>
          </DarwinModal>
        )}
      </DarwinRow>
    </>
  );
};
